import React from 'react';
import { Collapse, Typography } from 'antd';

import { AnswerKeyGate } from 'components';

// Plain-language explainers for the trainer report tabs. Copy is intentional
// product wording — no equations anywhere — keep edits in sync with the
// assessment methodology docs in pm.api.
//
// TERMINOLOGY. On screen the reviewed record a trader is scored against is the
// ANSWER KEY. The model, the API and the admin tooling still call it a
// benchmark, and those names are unchanged; but in a trading room a benchmark
// is an index, and trainers reading "scored against a reviewed benchmark"
// understood the S&P. The one place "benchmark" survives in trainer copy is
// where it means an actual market benchmark, which is nowhere on this screen.

export type ExplainerCopy = {
  title: string;
  body: string;
  bullets: string[];
};

/**
 * The rating bands. This one line turns a number into a hiring recommendation,
 * so it is the line that must disappear when the answer key behind the number
 * has not passed its quality audit — see `summaryExplainer` below.
 */
export const RATING_BANDS =
  'Ratings: 85+ strong hire signal · 75+ positive/coachable · 65+ mixed · ' +
  'below 65 below the answer key';

/** What replaces the bands while they may not be claimed. */
export const RATING_BANDS_WITHHELD =
  'Rating bands are withheld here: they describe a hiring signal, and the ' +
  'answer key behind these scores has not passed the check that would support ' +
  'one. The pillar numbers are still shown.';

export const EXPLAINERS: { [key: string]: ExplainerCopy } = {
  summary: {
    title: 'How the assessment works',
    body:
      "Each trader is scored against the scenario's answer key: a reviewed record of " +
      'what the headlines meant, which instruments they should have moved, and what a ' +
      'disciplined portfolio would have looked like each period. Three pillars make up ' +
      'the official score: Signal, Expression and Risk Construction. Two more, Updating ' +
      'and Tail & Liquidity, are shown for context but marked provisional: the platform ' +
      "doesn't yet capture every behaviour they need, so they are excluded from the " +
      'official composite. Profit and loss is deliberately NOT part of any pillar: a ' +
      "lucky trade shouldn't outrank a disciplined process.",
    bullets: [
      'Official composite = average of the first three pillars',
      RATING_BANDS,
      'PnL and NAV are shown for context only',
    ],
  },
  '1': {
    title: 'Did they read the news correctly?',
    body:
      'Every headline in the scenario has a reviewed answer key: which instruments it ' +
      "genuinely affects and how strongly. This pillar compares each trader's choices " +
      '(which news they dragged onto which instruments, and how bullish or bearish ' +
      'they set each conviction dial) against that key, period by period. Credit ' +
      'comes from connecting the right news to the right markets, leaning the right ' +
      'way, and reserving strong conviction for periods where the evidence genuinely ' +
      'lined up. Staying neutral when the news was conflicting earns credit too: ' +
      'knowing when there is no trade is part of the skill.',
    bullets: [
      'Mapping = how well their news-to-instrument links match the key (balance of correct links, missed links, false links)',
      'Direction & calibration = did the dial point the right way, with strength proportional to the evidence',
      'Selectivity = restraint in neutral or conflicting periods',
    ],
  },
  '2': {
    title: 'Did their positions match their views?',
    body:
      'Having the right view is only half the job. This pillar checks whether the ' +
      "portfolio actually expressed it. Each period we compare the trader's positions " +
      "to the answer key's model portfolio, built from the same information: right " +
      'direction per ' +
      'instrument, sizes proportional to conviction, options used when they were the ' +
      "efficient tool, and no positions that contradict the trader's own stated " +
      'views. Overall shape matters more than any single line: a book pointed the ' +
      'right way but badly sized scores better than a big bet against your own dial.',
    bullets: [
      "Risk-vector fit = overall similarity of their book to the answer key's model book",
      'Own-view coherence = trading against your own dial is penalised',
      'Soft-limit discipline = how much of the allowed risk framework was respected',
    ],
  },
  '3': {
    title: "Was the book built like a risk manager's?",
    body:
      'This pillar ignores whether views were right. It asks whether the portfolio ' +
      'was assembled with professional risk discipline: were sizes set from how much ' +
      'could be lost rather than headline notional; did positions that share the same ' +
      'underlying driver, like two dollar pairs or bonds plus a rates option, get ' +
      'recognised as one bet; and did overall risk usage adapt as the environment ' +
      'changed. A trader can be wrong about markets and still score well here, or ' +
      'right and score badly.',
    bullets: [
      "Sizing = loss-based position sizes vs the answer key's risk budget",
      'Common factors = spotting when several positions are secretly the same bet',
      'Adaptation = risk raised and lowered at sensible times',
    ],
  },
  '4': {
    title: 'Did new evidence change their mind?',
    body:
      'Markets reward those who update. Comparing consecutive periods, this pillar ' +
      'checks whether conviction dials and positions moved when the story changed: ' +
      'cutting or reversing when the evidence flipped against a view, standing still ' +
      'when nothing meaningful changed, and avoiding churn for its own sake. It is ' +
      "marked provisional because the platform doesn't yet record the trader's " +
      'stated reasoning; planned thesis cards and invalidation rules will complete ' +
      'it.',
    bullets: [
      'Hard invalidation = evidence flipped sign. Did they respond',
      'Thesis decay = a story faded. Did they take risk down',
      'No-action = quiet periods. Did they leave things alone',
    ],
  },
  '5': {
    title: 'Would the book survive a bad week?',
    body:
      "Each period's opening book is pushed through six severe but plausible market " +
      'shocks (an inflation spike, a growth scare, a funding squeeze and others) ' +
      'including the cost of getting out of positions under stress. The score ' +
      'reflects the worst of those outcomes: a portfolio that would lose a survivable ' +
      'amount even in its worst scenario scores well; one that would be crippled ' +
      'scores poorly, no matter how it actually performed. Options that genuinely ' +
      'protect the book earn credit; options that merely double the same bet do not. ' +
      "Provisional because stop-loss behaviour isn't yet fully captured.",
    bullets: [
      'Path survival = worst-case loss vs the capital base',
      'Executable exit = could the book be unwound without punishing costs',
      'Protection = did hedges actually reduce the worst case',
    ],
  },
};

/**
 * The summary explainer as it must read for THIS session's answer key.
 *
 * Two things change when the answer key failed its quality audit, or was
 * never checked at all: the rating bands come out (they are a hiring claim the
 * evidence refuses), and the reader is sent to the banner above the table
 * rather than being left to infer that a confident-looking number is safe.
 */
export const summaryExplainer = (gate: AnswerKeyGate | null): ExplainerCopy => {
  const base = EXPLAINERS.summary;
  if (!gate || !gate.suppressLegend) return base;
  return {
    ...base,
    body:
      base.body +
      ' Read the notice above the table before quoting any of these numbers: ' +
      "it says what the quality audit found about this scenario's answer key.",
    bullets: base.bullets.map(bullet =>
      bullet === RATING_BANDS ? RATING_BANDS_WITHHELD : bullet,
    ),
  };
};

/** Collapsible explainer (open by default) shown at the top of each tab. */
const Explainer: React.FC<{ copy: ExplainerCopy }> = ({ copy }) => (
  <Collapse
    className="assessment-report-explainer"
    defaultActiveKey={['explainer']}
    items={[
      {
        key: 'explainer',
        label: (
          <span className="assessment-report-explainer-title">
            {copy.title}
          </span>
        ),
        children: (
          <>
            <Typography.Paragraph>{copy.body}</Typography.Paragraph>
            <Typography.Text strong>How to read this</Typography.Text>
            <ul className="assessment-report-explainer-bullets">
              {copy.bullets.map(bullet => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </>
        ),
      },
    ]}
  />
);

export default Explainer;
